import React, { useState } from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  SvgIcon,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Search as SearchIcon } from 'react-feather';
import {
  CreateManufacturerRequest,
  CustomInputEvent,
  CustomKeyboardEvent
} from 'src/types';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import { usePermissions, useSnackBar } from 'src/hooks';
import {
  containsAlphabetsWithSpacesAndDashes,
  isEmptyOrSpaces
} from 'src/utils';
import { ManufacturerAddDialog } from './components/ManufacturerAddDialog';

const { actions: manufacturerActions } = slices.manufacturer;
interface Props {
  className?: string;
  getManufacturers: (keyword?: string) => void;
  onSearchManufacturer: (text: string) => void;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  cardContent: {
    flexDirection: 'row'
  }
}));

const Toolbar = ({
  className,
  onSearchManufacturer,
  getManufacturers,
  ...rest
}: Props) => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const { canCreateManufacturer } = usePermissions();

  const [searchText, setSearchText] = useState('');
  const [
    isCreateManufacturerVisible,
    setIsCreateManufacturerVisible
  ] = useState<boolean>(false);
  const [addManufacturerLoading, setAddManufacturerLoading] = useState<boolean>(
    false
  );

  const onOpenCreateManufacturerDialog = () => {
    setIsCreateManufacturerVisible(true);
  };

  const onCloseCreateManufacturerDialog = () => {
    setIsCreateManufacturerVisible(false);
    setAddManufacturerLoading(false);
  };

  const onAddManufacturerOnModalPress = async (
    payload: CreateManufacturerRequest
  ) => {
    const { name } = payload;
    // TODO: Add checker loader before close.
    setAddManufacturerLoading(true);
    if (
      !name ||
      isEmptyOrSpaces(name) ||
      !containsAlphabetsWithSpacesAndDashes(name)
    ) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide proper Manufacturer name'
      });
      setAddManufacturerLoading(false);
      return false;
    }
    if (name) {
      const response = unwrapResult(
        await dispatch(manufacturerActions.createManufacturerThunk(payload))
      );
      if (response.success) {
        snackBar.show({ severity: 'success', message: response.message });
        getManufacturers();
        onCloseCreateManufacturerDialog();
        return true;
      }
      snackBar.show({ severity: 'error', message: response.message });
      setAddManufacturerLoading(false);
      return false;
    }
    snackBar.show({
      severity: 'error',
      message: 'Please fill up all required information.'
    });
    return false;
  };

  const handleChange = (event: CustomInputEvent) => {
    setSearchText(event.target.value);
  };

  const onSearchClick = () => {
    onSearchManufacturer(searchText);
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchManufacturer(searchText);
    }
  };

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Typography color="textPrimary" gutterBottom variant="h3">
          Manufacturers
        </Typography>
        {canCreateManufacturer ? (
          <Button
            onClick={onOpenCreateManufacturerDialog}
            color="primary"
            variant="contained"
          >
            add manufacturer
          </Button>
        ) : null}
      </Box>
      <Box mt={3}>
        <Card>
          <CardContent>
            <Grid alignItems="center" container spacing={2}>
              <Grid item lg={4} md={6} xs={12}>
                <Box maxWidth={500}>
                  <TextField
                    fullWidth
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon fontSize="small" color="action">
                            <SearchIcon />
                          </SvgIcon>
                        </InputAdornment>
                      )
                    }}
                    placeholder="Search manufacturer"
                    variant="outlined"
                    onKeyPress={onKeyPress}
                  />
                </Box>
              </Grid>
              <Grid alignItems="center" item xs={1}>
                <Button
                  onClick={onSearchClick}
                  color="primary"
                  variant="contained"
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {canCreateManufacturer ? (
          <ManufacturerAddDialog
            isVisible={isCreateManufacturerVisible}
            loading={addManufacturerLoading}
            onAddPress={onAddManufacturerOnModalPress}
            handleClose={onCloseCreateManufacturerDialog}
          />
        ) : null}
      </Box>
    </div>
  );
};

export default Toolbar;
